import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { SavedGroupsService } from '../../services/orders/saved-groups.service';

export interface GroupEditItemView {
  uuid: string;
  name: string;
  qty: number;
  price: number;
}

export interface GroupEditDialogData {
  gid: string;
  name?: string;
  color?: string;
  items: GroupEditItemView[];
  canDelete: boolean; // false when payment associated
}

export interface GroupEditDialogResult {
  action: 'save' | 'delete';
  name?: string;
  keepItemUuids?: string[]; // when save: items kept in the group
}

@Component({
  selector: 'app-group-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
  ],
  template: `
    <h2 mat-dialog-title>Editar grupo</h2>
    <div mat-dialog-content>
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>Nombre del grupo</mat-label>
        <input matInput [(ngModel)]="editableName" placeholder="Ej: Juan, Mesa 6" />
      </mat-form-field>

      <div class="mt-3 mb-2" style="font-weight:600">Ítems asociados</div>
      <div class="item-list">
        <div class="cards" *ngIf="editableItems.length > 0; else emptyItems">
          <div class="card" *ngFor="let it of editableItems">
            <div class="card-top">
              <ng-container *ngIf="data.canDelete">
                <ng-container *ngIf="!it.loading; else spinnerTpl">
                  <mat-checkbox class="sel" [(ngModel)]="it.checked" [disabled]="!!it.loading" (change)="onToggleItem(it)"></mat-checkbox>
                </ng-container>
                <ng-template #spinnerTpl>
                  <mat-icon class="spin muted">autorenew</mat-icon>
                </ng-template>
              </ng-container>
              <div class="name">{{ it.name }}</div>
            </div>
            <div class="card-bottom">
              <div class="amount">
                {{ '$' }}{{ it.price }}
                <ng-container *ngIf="it.qty > 1"> × {{ it.qty }} = {{ '$' }}{{ it.price * it.qty }}</ng-container>
              </div>
            </div>
          </div>
          <div class="cards-total">Total {{ '$' }}{{ totalAmount }}</div>
        </div>
        <ng-template #emptyItems>
          <div class="muted">Este grupo no tiene ítems.</div>
        </ng-template>
      </div>
    </div>
    <div mat-dialog-actions align="end" class="justify-content-end">
      <button mat-stroked-button (click)="onCancel()">Cancelar</button>
      <button mat-flat-button color="primary" (click)="onSave()">Guardar</button>
    </div>
  `,
  styles: [
    `
    .item-list{max-height:55vh; overflow:auto; padding-right:6px}
    .cards{display:grid; grid-template-columns:repeat(auto-fill, minmax(240px, 1fr)); gap:8px}
    .card{border:1px solid #e2e8f0; border-radius:8px; padding:10px; background:#fff}
    .card-top{display:flex; align-items:flex-start; gap:8px}
    .name{font-size:13px; line-height:1.25; overflow-wrap:anywhere}
    .sel{--mdc-checkbox-selected-icon-color:#ff6b6b;}
    .card-bottom{margin-top:6px; display:flex; justify-content:flex-end; align-items:center}
    .amount{font-size:13px; font-weight:600; color:#0f172a; text-align:right}
    .cards-total{margin-top:6px; text-align:right; font-weight:600; border-top:1px solid #e2e8f0; padding-top:6px}
    .muted{color:#64748b; font-size:12px}
    .flex-spacer{flex:1}
    .spin{animation:spin 1s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}
    `
  ]
})
export class GroupEditDialogComponent {
  editableName = '';
  editableItems: Array<GroupEditItemView & { checked: boolean; loading?: boolean }> = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GroupEditDialogData,
    private dialogRef: MatDialogRef<GroupEditDialogComponent, GroupEditDialogResult | undefined>,
    private savedGroupsService: SavedGroupsService,
  ) {
    this.editableName = (data?.name || '').toString();
    this.editableItems = (data?.items || []).map(it => ({ ...it, checked: true }));
  }

  onCancel() {
    this.dialogRef.close(undefined);
  }

  onSave() {
    const keep = this.data?.canDelete
      ? this.editableItems.filter(i => i.checked).map(i => i.uuid)
      : this.editableItems.map(i => i.uuid);
    const result: GroupEditDialogResult = {
      action: 'save',
      name: (this.editableName ?? '').toString().trim(),
      keepItemUuids: keep,
    };
    this.dialogRef.close(result);
  }

  // Eliminación movida al badge del grupo; el modal sólo edita nombre y asociación

  onToggleItem(it: GroupEditItemView & { checked: boolean; loading?: boolean }): void {
    try {
      if (!this.data?.canDelete) return;
      // Si se desmarca, invocar DELETE y mostrar spinner
      if (!it.checked) {
        it.loading = true;
        this.savedGroupsService.removeItemFromGroup(this.data.gid, it.uuid).subscribe({
          next: () => { it.loading = false; },
          error: () => { it.loading = false; it.checked = true; }
        });
      } else {
        // Si se marca, invocar POST y mostrar spinner
        it.loading = true;
        this.savedGroupsService.addItemsToGroup(this.data.gid, [it.uuid]).subscribe({
          next: () => { it.loading = false; },
          error: () => { it.loading = false; it.checked = false; }
        });
      }
    } catch {
      it.loading = false;
      it.checked = true;
    }
  }

  get totalAmount(): number {
    const items = this.data?.canDelete
      ? this.editableItems.filter(i => i.checked)
      : this.editableItems;
    return items.reduce((sum, it) => sum + ((Number(it.price) || 0) * (Number(it.qty) || 0)), 0);
  }
}